import { ChangeDetectionStrategy, Component, DestroyRef, inject, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { LegacyHealthCheckService, StorageService } from '@coin/shared/core/data-access';
import { ContentLanguage, StorageKey } from '@coin/shared/core/util-enums';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, catchError, combineLatest, map, Observable, throwError } from 'rxjs';
import { AuthService } from '@coin/shared/auth/data-access';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UpdateNotificationsService } from '@coin/shared/core/util-helpers';
import { Store } from '@ngxs/store';
import { Topic } from '@coin/shared/core/util-models';
import { BannerState } from './home/state/banner.state';
import { LoadTopics, UpdateBannerVisibility } from './home/state/banner.actions';
import { SidebarComponent } from './static/sidebar/sidebar.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AppComponent {
  sidebarComponent = viewChild(SidebarComponent);

  private translateService = inject(TranslateService);
  private storageService = inject(StorageService);
  private authService = inject(AuthService);
  private store = inject(Store);
  private healthCheckService = inject(LegacyHealthCheckService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  public isHeaderVisible = false;
  public bannerTopics$ = this.store.select<Topic[]>(BannerState.visibleBannerTopics);

  public healthCheckFail$ = new BehaviorSubject<boolean>(false);
  public isSideBarVisible$: Observable<boolean> = combineLatest([
    this.healthCheckFail$,
    this.authService.isAuthenticated$,
    this.authService.isAuthorized$
  ]).pipe(
    map(([healthCheckFail, isAuthenticated, isAuthorized]) => !healthCheckFail && isAuthenticated && isAuthorized)
  );

  public get sideBarCollapsed$(): Observable<boolean> {
    return this.sidebarComponent()!.sideBarCollapsed$;
  }

  constructor() {
    inject(UpdateNotificationsService).checkForUpdates();
    this.translateService.setDefaultLang(ContentLanguage.ENGLISH);
    this.setupLang();
    this.checkBackendStatus();
    this.listenToAuthorizedStatus();
    this.store.dispatch(new LoadTopics());
    this.isHeaderVisible = !window.location.href.includes('/login');
  }

  private listenToAuthorizedStatus(): void {
    this.authService.isAuthorized$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isAuthorized => {
      if (!isAuthorized) {
        this.router.navigate(['unauthorized']);
      }
    });
  }

  private setupLang(): void {
    const lang = this.storageService.get(StorageKey.LANGUAGE);

    if (lang) {
      this.translateService.reloadLang(lang);
    }

    this.translateService.use(lang ?? ContentLanguage.ENGLISH);
  }

  private checkBackendStatus(): void {
    this.healthCheckService
      .ping()
      .pipe(
        catchError(err => {
          this.healthCheckFail$.next(true);
          this.router.navigate(['fallback'], { skipLocationChange: true });
          return throwError(() => err);
        })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  public closeBanner(topic: Topic): void {
    this.store.dispatch(new UpdateBannerVisibility(topic));
  }
}
